/**
 * Export and backup: CSV for the month, JSON for the whole database.
 *
 * Every file is produced in the browser from a Blob. Nothing is uploaded.
 */

import { downloadBlob } from './dom.js';
import { formatMonth } from './dates.js';
import { billableValue, locationLabel, workstreamsForDay } from './model.js';
import { exportAll, markBackupTaken } from './store.js';

/**
 * Quote one CSV field.
 *
 * Fields are always quoted: it is simpler to read, and it stops a leading
 * `=` or `+` in a note being treated as a formula by a spreadsheet.
 */
function csvField(value) {
  const text = value === null || value === undefined ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

/**
 * Build a CSV document. Uses CRLF line endings and a UTF-8 byte order mark,
 * which is what Excel on Windows and macOS expects for accented characters.
 */
export function toCsv(headers, rows) {
  const lines = [headers.map(csvField).join(','), ...rows.map((row) => row.map(csvField).join(','))];
  return '﻿' + lines.join('\r\n') + '\r\n';
}

function csvBlob(text) {
  return new Blob([text], { type: 'text/csv;charset=utf-8' });
}

/** One row per day: date, day type, location, workstreams, done summary. */
export function buildDaysCsv(days) {
  const headers = ['Date', 'Day type', 'Location', 'Billable days', 'Workstreams', 'Done summary', 'Notes'];
  const rows = days
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((day) => [
      day.date,
      day.dayType,
      locationLabel(day),
      billableValue(day),
      workstreamsForDay(day).join(' | '),
      day.done.map((e) => e.text.trim()).filter(Boolean).join(' | '),
      day.notes.replace(/\s*\n\s*/g, ' / ').trim(),
    ]);
  return toCsv(headers, rows);
}

/** One row per expense, carrying the date it belongs to. */
export function buildExpensesCsv(days) {
  const headers = ['Date', 'Category', 'Amount (GBP)', 'Distance (km)', 'Note', 'Receipt reference'];
  const rows = [];
  for (const day of days.slice().sort((a, b) => a.date.localeCompare(b.date))) {
    for (const expense of day.expenses) {
      rows.push([
        day.date,
        expense.category,
        (Number(expense.amount) || 0).toFixed(2),
        expense.km === '' || expense.km === null || expense.km === undefined ? '' : Number(expense.km),
        expense.note,
        expense.receiptRef,
      ]);
    }
  }
  return toCsv(headers, rows);
}

/** Download both CSV files for a month. */
export function exportMonthCsv(monthKey, days) {
  const label = formatMonth(monthKey).replace(' ', '-').toLowerCase();
  downloadBlob(csvBlob(buildDaysCsv(days)), `timesheet-days-${label}.csv`);
  // Stagger slightly: Safari ignores a second download fired in the same tick.
  setTimeout(() => {
    downloadBlob(csvBlob(buildExpensesCsv(days)), `timesheet-expenses-${label}.csv`);
  }, 400);
  return { days: days.length, expenses: days.reduce((n, d) => n + d.expenses.length, 0) };
}

// --- JSON backup ------------------------------------------------------------

/** Download a full backup and record that one has been taken. */
export async function downloadBackup() {
  const payload = await exportAll();
  const stamp = new Date().toISOString().slice(0, 10);
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  downloadBlob(blob, `timesheet-backup-${stamp}.json`);
  await markBackupTaken();
  return payload;
}

/** Parse and sanity-check a backup file before anything is written. */
export function parseBackup(text) {
  let payload;
  try {
    payload = JSON.parse(text);
  } catch {
    throw new Error('That file is not valid JSON.');
  }
  if (!payload || typeof payload !== 'object') {
    throw new Error('That file does not look like a timesheet backup.');
  }
  if (payload.app !== 'timesheet' || !Array.isArray(payload.days)) {
    throw new Error('That file does not look like a timesheet backup. Expected a file created by this app.');
  }
  return payload;
}
